import React, { useEffect, useRef } from 'react';

// SMS opt-in modal. Two steps:
//   'phone' — mobile number + the required A2P consent disclosures.
//   'code'  — 6-digit verification code (+ resend).
//
// All state (phone/code/busy/error/notice) lives in the parent (Alerts.js);
// this component is presentational + wires the callbacks. Closing the modal
// does NOT wipe parent state, so a half-finished verification is resumable
// (the parent derives a "pending" state from Firestore and can reopen us at
// the 'code' step).
//
// Styling: uses the app's theme tokens via classes in Alerts.css, so it
// adapts to light/dark automatically.
export default function SmsOptInModal({
  open, step, savedPhone,
  phoneInput, setPhoneInput,
  codeInput, setCodeInput,
  busy, error, notice,
  onSendCode, onVerify, onResend, onClose,
}) {
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const id = window.setTimeout(() => { firstFieldRef.current?.focus(); }, 50);
    return () => { document.removeEventListener('keydown', onKey); window.clearTimeout(id); };
  }, [open, step, onClose]);

  if (!open) return null;

  return (
    <div
      className="sms-modal-scrim"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="sms-modal" role="dialog" aria-modal="true" aria-labelledby="sms-modal-title">
        <button type="button" className="sms-modal-close" aria-label="Close" onClick={onClose}>×</button>

        {step === 'code' ? (
          <>
            <h2 id="sms-modal-title" className="sms-modal-title">Enter your code</h2>
            <p className="sms-modal-sub">
              We texted a 6-digit code to <strong>{savedPhone}</strong>. Enter it below to confirm your number.
            </p>
            <input
              ref={firstFieldRef}
              className="alerts-recipient-input"
              style={{ width: '100%', letterSpacing: '0.3em', textAlign: 'center', fontSize: 20 }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
            />
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: '100%', marginTop: 14 }}
              disabled={busy || codeInput.length < 4}
              onClick={onVerify}
            >
              {busy ? 'Checking…' : 'Verify'}
            </button>
            <button
              type="button"
              className="linklike"
              style={{ display: 'block', width: '100%', marginTop: 12, textAlign: 'center' }}
              disabled={busy}
              onClick={onResend}
            >
              Resend code
            </button>
          </>
        ) : (
          <>
            <h2 id="sms-modal-title" className="sms-modal-title">Set up text alerts</h2>
            <p className="sms-modal-sub">
              Enter your mobile number and we’ll text a 6-digit code to confirm it’s yours.
            </p>
            <input
              ref={firstFieldRef}
              className="alerts-recipient-input"
              style={{ width: '100%' }}
              type="tel"
              autoComplete="tel"
              placeholder="(808) 555-1234"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
            />
            <div className="sms-modal-consent">
              By tapping “Send code” and verifying your number, you agree to receive recurring automated
              water-tank alert text messages from SoKo Water Systems at the number provided. Consent is not
              a condition of any purchase. Message frequency varies. Message &amp; data rates may apply.
              Reply STOP to unsubscribe, HELP for help. See our{' '}
              <a href="/terms.html" target="_blank" rel="noreferrer">Terms &amp; Conditions</a> and{' '}
              <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </div>
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={busy}
              onClick={onSendCode}
            >
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </>
        )}

        {notice && <div className="sms-modal-notice">{notice}</div>}
        {error && <div className="sms-modal-error">{error}</div>}
      </div>
    </div>
  );
}
